import { ArrowDownTrayIcon } from '@heroicons/react/20/solid';
import type { RowData } from '@tanstack/react-table';
import Papa from 'papaparse';
import { useState } from 'react';
import { downloadBlob } from '../../helpers/download-file';
import { IconButton } from '../buttons';
import { isStrictColumnDef } from './column';
import type { AppTable } from './features';

type TanStackTableCSVButtonProps<T extends RowData> = {
  table: AppTable<T> | null;
  filename: string;
};

const excludeColumns = ['', '#', 'Deals Savings', 'Volume Savings', 'Actions', 'Deal'];

export function TanStackTableCSVButton<T extends RowData>({
  table,
  filename,
}: TanStackTableCSVButtonProps<T>) {
  const [generating, setGenerating] = useState(false);

  if (!table) {
    return null;
  }

  const handleClick = () => {
    setGenerating(true);
    // Defer one frame so the disabled/loading state paints before the heavy
    // serialization work blocks the main thread.
    requestAnimationFrame(() => {
      try {
        const { rows, columns } = buildCsvRows(table);
        const csv = Papa.unparse(rows, { columns });
        downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
      } finally {
        setGenerating(false);
      }
    });
  };

  return (
    <IconButton tooltip="Download CSV" onClick={handleClick} disabled={generating}>
      <ArrowDownTrayIcon
        className={`size-5 ${generating ? 'animate-pulse' : ''}`}
        aria-hidden="true"
      />
    </IconButton>
  );
}

function buildCsvRows<T extends RowData>(
  table: AppTable<T>
): {
  rows: Record<string, string>[];
  columns: string[];
} {
  const visibleColumns = table
    .getVisibleLeafColumns()
    .map((col) => col.columnDef)
    .filter(isStrictColumnDef)
    .filter((c) => !excludeColumns.includes(c.id));
  const items = table.getRowModel().rows.map((row) => row.original);

  const seen = new Set<string>();
  const columns: string[] = [];
  const rows = items.map((item) => {
    const row: Record<string, string> = {};
    for (const column of visibleColumns) {
      if (column.meta.toCSV) {
        for (const [key, value] of Object.entries(column.meta.toCSV(item))) {
          row[key] = value;
          if (!seen.has(key)) {
            seen.add(key);
            columns.push(key);
          }
        }
      } else {
        row[column.id] = column.meta.toString(item);
        if (!seen.has(column.id)) {
          seen.add(column.id);
          columns.push(column.id);
        }
      }
    }

    return row;
  });

  return { rows, columns };
}
